/**
 * WZ key-event tracking for app.humanriskacademy.com.
 *
 * Emits the shared WZ analytics event taxonomy as `dataLayer` pushes. This is
 * a CONTRACT with the marketing site (w-z.com / wz-cms-astro): both domains
 * push IDENTICALLY-NAMED events with the same payload shapes so GA4 — fed by
 * the shared GTM container — counts one cross-domain funnel. The canonical
 * taxonomy lives in the `analytics-events` skill; keep the two repos in sync.
 *
 * Why these push UNCONDITIONALLY (unlike the GTM loader in `gtm.ts`, which
 * no-ops without VITE_GTM_ID): a dataLayer push is just an array append — it
 * sets no cookies and needs no consent. Pushing now means events are already
 * queued when the GTM container goes live; GTM replays the existing dataLayer
 * on load, so nothing emitted before the container loads is lost. We decouple
 * "emit the event" (here, today) from "route the event" (GTM UI, later).
 *
 * Event taxonomy:
 *   begin_checkout      registration started   (GA4 ecommerce — carries value/items)
 *   purchase            registration completed (GA4 ecommerce — value/items/transaction_id)
 *   class_schedule_view class/training schedule viewed
 *   contact_submit      contact form submitted
 *   quote_request       quote/group-training request submitted
 *   phone_click         tel: link clicked    (page path only — never the number)
 *   email_click         mailto: link clicked (page path only — never the address)
 */

import { gtag } from './gtag';
import { isGa4Direct } from './gtm';

type DataLayerEvent = Record<string, unknown> & { event: string };

/**
 * Emit one key event.
 *
 * Two consumers read the same `dataLayer` and they do NOT read it the same way:
 * GTM processes object-shaped pushes; gtag.js only processes `gtag(...)` command
 * tuples and silently ignores objects. So while the direct-GA4 flag is on we
 * emit both — otherwise a direct GA4 tag records pageviews and nothing else.
 *
 * `gtagParams` exists because the two disagree on ecommerce shape: GTM wants it
 * nested under `ecommerce`, gtag wants it flat.
 */
function emit(name: string, gtmPayload: Omit<DataLayerEvent, 'event'>, gtagParams?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // Reset the ecommerce object before an ecommerce push. GTM keeps a MERGED,
  // persistent model of the dataLayer and merges arrays by index — so the
  // 2-item `items` from a prior begin_checkout would bleed into a 1-item
  // purchase. Google's documented fix is to push `{ ecommerce: null }` first.
  if ('ecommerce' in gtmPayload) window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event: name, ...gtmPayload });
  if (isGa4Direct()) gtag('event', name, gtagParams ?? gtmPayload);
}

/** Append to the GTM dataLayer, creating it if the container hasn't loaded. */
function pushToDataLayer(payload: DataLayerEvent): void {
  const { event, ...rest } = payload;
  emit(event, rest);
}

// ---- Ecommerce: the two revenue events ----

export interface CheckoutItem {
  item_id: string | number;
  item_name: string;
  price?: number;
  quantity?: number;
}

export interface CheckoutPayload {
  value: number;
  currency?: string; // default 'USD'
  items: CheckoutItem[];
}

/**
 * Registration start — GA4 `begin_checkout`. Fire when the visitor commits to
 * registering (free) or opens the payment step (paid), before any navigation.
 */
export function trackBeginCheckout(order: CheckoutPayload): void {
  const flat = { currency: order.currency ?? 'USD', value: order.value, items: order.items };
  emit('begin_checkout', { ecommerce: flat }, flat);
}

/** Completed registration — GA4 `purchase`. Fire once on the success page. */
export function trackPurchase(order: CheckoutPayload & { transaction_id: string }): void {
  const flat = {
    transaction_id: order.transaction_id,
    currency: order.currency ?? 'USD',
    value: order.value,
    items: order.items,
  };
  emit('purchase', { ecommerce: flat }, flat);
}

// ---- Engagement / lead events ----

export function trackClassScheduleView(listName = 'Class Schedule'): void {
  pushToDataLayer({ event: 'class_schedule_view', list_name: listName });
}

export function trackContactSubmit(formLocation?: string): void {
  pushToDataLayer({ event: 'contact_submit', form_location: formLocation });
}

export function trackQuoteRequest(formLocation?: string): void {
  pushToDataLayer({ event: 'quote_request', form_location: formLocation });
}

/**
 * Contact clicks report WHERE they happened, never WHO was contacted. Google's
 * measurement policy bars email addresses and phone numbers as event
 * parameters, so `linkLocation` must stay a page path — passing the anchor's
 * text would smuggle the address back in, since a mailto: link's label is
 * routinely the address itself.
 */
export function trackPhoneClick(linkLocation?: string): void {
  pushToDataLayer({ event: 'phone_click', link_location: linkLocation });
}

export function trackEmailClick(linkLocation?: string): void {
  pushToDataLayer({ event: 'email_click', link_location: linkLocation });
}

// ---- Account creation ----

/** An account older than this is a returning visitor, not a signup. */
const SIGNUP_MAX_AGE_MS = 60 * 60 * 1000;
const SIGNUP_SENT_KEY = 'wz_signup_sent';

/**
 * GA4 `sign_up` — the only join between acquisition source and account creation.
 *
 * There is no server-side "this request created the account" flag to key off:
 * `users.sign_in_count` is bumped once per calendar DAY, so it stays at 1 for a
 * new user's entire first day and would re-fire on every page load. Instead we
 * use `created_at` recency plus a per-user localStorage guard, so idempotency
 * lives on the client, where the event lives.
 *
 * Accepted limitation: signing up, clearing storage, and returning within the
 * hour double-fires. GA4 does not de-duplicate `sign_up` regardless.
 */
export function trackSignUp(userId: number | string, createdAt: string): void {
  if (typeof window === 'undefined') return;

  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return;
  if (Date.now() - created > SIGNUP_MAX_AGE_MS) return;

  const key = `${SIGNUP_SENT_KEY}:${userId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    /* storage disabled — fall through and emit; at worst it repeats */
  }

  pushToDataLayer({ event: 'sign_up', method: 'auth0' });
}

// ---- Pending-purchase bridge (survives Stripe round-trip + SPA navigation) ----

const PENDING_PURCHASE_KEY = 'wz_pending_purchase';

/**
 * Stash the order at `begin_checkout` so the success page can emit an accurate
 * `purchase` (value/items) after a full-page navigation — including the Stripe
 * Checkout round-trip, which leaves and returns to this origin.
 */
export function stashPendingPurchase(order: CheckoutPayload): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(order));
  } catch {
    /* storage disabled or over quota — purchase will still fire, sans value */
  }
}

/**
 * Read and clear the stashed order. Clearing is what makes `purchase` fire at
 * most once: a refresh of the success page finds nothing and won't re-count.
 */
export function consumePendingPurchase(): CheckoutPayload | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
    return JSON.parse(raw) as CheckoutPayload;
  } catch {
    return null;
  }
}

// ---- Purchase-on-redirect (shared by every Stripe/offline success surface) ----

/**
 * Emit `purchase` after a checkout redirect, exactly once.
 *
 * There are four success surfaces (`/my-courses`, `/courses`,
 * `/organization/packages`, `/organization`) and they must not each reinvent
 * this. Call it from inside the page's existing success branch.
 *
 * `transaction_id` is a REAL id or nothing:
 *   - card    → Stripe's `{CHECKOUT_SESSION_ID}` from the success_url
 *   - offline → the registration id we append on redirect
 * A random UUID is worse than no id, because it looks valid while defeating
 * GA4's purchase de-duplication and any Stripe↔BigQuery join.
 *
 * If the stashed order is gone (fresh tab, storage disabled) we have no value or
 * items, so we skip rather than record a $0 conversion — a purchase at zero
 * corrupts the revenue series more than a missing one does.
 *
 * Consuming the stash is what makes this idempotent: refreshing the success URL
 * finds nothing and cannot double-count.
 */
export function firePurchaseFromRedirect(search: URLSearchParams): void {
  const sessionId = search.get('stripe_checkout_session_id');
  const registrationId = search.get('registration_id');
  const transactionId = sessionId || (registrationId ? `reg-${registrationId}` : null);
  if (!transactionId) return;

  const pending = consumePendingPurchase();
  if (!pending) return;

  trackPurchase({ transaction_id: transactionId, ...pending });
}

// ---- Auto link-click tracking (one listener for all tel:/mailto:) ----

let linkTrackingBound = false;

/**
 * Bind a single delegated, capture-phase click listener that turns every
 * `tel:` / `mailto:` anchor click into a `phone_click` / `email_click` — so we
 * never have to wire each of the dozens of contact links by hand. Idempotent.
 */
export function initLinkClickTracking(): void {
  if (linkTrackingBound || typeof document === 'undefined') return;
  linkTrackingBound = true;
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a[href^="tel:"], a[href^="mailto:"]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute('href') ?? '';
      const page = window.location.pathname;
      if (href.startsWith('tel:')) trackPhoneClick(page);
      else if (href.startsWith('mailto:')) trackEmailClick(page);
    },
    { capture: true },
  );
}
